/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState } from "react";
import { getAuth, signInWithEmailAndPassword, sendPasswordResetEmail } from "firebase/auth";
import { useNavigate } from "react-router-dom";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Swal from "sweetalert2";


// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import SoftSnackbar from "components/SoftSnackbar";
import SoftBadge from "components/SoftBadge";
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';

// Authentication layout components
import BasicLayout from "layouts/authentication/components/BasicLayout";
import Socials from "layouts/authentication/components/Socials";
import Separator from "layouts/authentication/components/Separator";

// Images
import curved9 from "assets/images/curved-images/curved9.jpg";
import { Tooltip } from "@mui/material";

function Basic() {
  const [email, setEmail] = useState('');   // State for email
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const navigate = useNavigate();
  const [forgotEmail, setForgotEmail] = useState(""); 
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const toggleForgotPassword = () => setShowForgotPassword(!showForgotPassword);

  const [snackbar, setSnackbar] = useState({
    show: false,
    message: "",
    title: "",
  });
  
  const showSnackbar = (title, message ) => {
    setSnackbar({ show: true, title, message });
  };

  const hideSnackbar = () => {
    setSnackbar({ ...snackbar, show: false });
  };

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/invalid-credential':
        return 'Invalid Login credentials';
      case 'auth/weak-password':
        return 'The password is too weak. Please use a stronger password.';
      // ... other error codes ...
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const signInHandler = () => {
    event.preventDefault();
    const auth = getAuth();
    signInWithEmailAndPassword(auth, email, password)
      .then((userCredential) => {
        // Signed in 
        const user = userCredential.user;
        console.log("UC: ", userCredential)
        console.log("user", user)
        navigate('/pages/profile-overview');
        // Redirect or perform additional actions
      })
      .catch((error) => {
        console.log("error:", error)
        const errorMessage = getErrorMessage(error.code);
        showSnackbar("Sign in error:", errorMessage)
      });
  };

  const handleResetPassword = (event) => {
    event.preventDefault();
    const auth = getAuth();
    sendPasswordResetEmail(auth, forgotEmail)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Email Sent',
          text: 'Check your email in order to reset.',
        });
      })
      .catch((error) => {
        // Error occurred
        console.error("Error sending password reset email:", error);
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: 'Failed to send password reset email. Please try again.',
        });  
      });
  };

  return (
    <BasicLayout
      title="Welcome back!"
      description="Continue to your account by logging in."
      image={curved9}
    >
      <Card>
        <SoftBox p={3} mb={1} textAlign="center">
          <SoftTypography variant="h5" fontWeight="medium">
            Sign in
          </SoftTypography>
        </SoftBox>
        <SoftBox mb={2}>
          <Socials action="signin" />
        </SoftBox>
        <SoftBox pt={3} px={3} pb={2}>
          <SoftBox component="form" role="form">
            <SoftBox mb={2}>
              <SoftInput type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </SoftBox>
            <SoftBox mb={2}>
              <SoftInput type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </SoftBox>
            <SoftBox display="flex" alignItems="center">
              <Switch checked={rememberMe} onChange={handleSetRememberMe} />
              <SoftTypography
                variant="button"
                fontWeight="regular"
                onClick={handleSetRememberMe}
                sx={{ cursor: "pointer", userSelect: "none" }}
              >
                &nbsp;&nbsp;Remember me
              </SoftTypography>
            </SoftBox>
            <SoftBox mt={4} mb={1}>
              <SoftButton variant="gradient" color="info" fullWidth onClick={signInHandler}>
                sign in
              </SoftButton>
            </SoftBox>
            <Separator />
            <SoftBox mt={1} mb={3}>
              <SoftButton
                component={Link}
                to="/authentication/sign-up/basic"
                variant="gradient"
                color="dark"
                fullWidth
              >
                sign up
              </SoftButton>
            </SoftBox>
              <SoftBox pt={1} display="flex" justifyContent="center" alignItems="center" >
                <SoftTypography onClick={toggleForgotPassword} variant="button" color="text" sx={{ textDecoration: 'underline', cursor: 'pointer'}} >
                  Forgot Password
                </SoftTypography>
              </SoftBox>
              { showForgotPassword && (
                <>
                <SoftBox pt={4} display="flex" flexDirection="column" justifyContent="center" alignItems="center" > 
                  <SoftBadge
                      variant="contained"
                      color="warning"
                      size="lg"
                      badgeContent={
                        <SoftTypography variant="h6" color="dark" >
                          Reset your Password
                        </SoftTypography>
                      }
                      //circular
                      //container
                  />
                </SoftBox>
                <SoftBox pt={3} display="flex" flexDirection="column" justifyContent="flex-start"  > 
                  <Tooltip title="Reset email will be send to this email adress" placement="top">
                  <SoftInput
                    fullWidth
                    placeholder="youremail@gmail.com"
                    variant="outlined"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                  />
                  </Tooltip>
                </SoftBox>
                <SoftBox display="flex" pt={2} px={1} justifyContent='center' >
                  <SoftButton startIcon={ <ForwardToInboxOutlinedIcon sx={{ mr: 1 }} />} color="dark" size="medium" variant="outlined" onClick={handleResetPassword} >
                    Send Reset Email 
                  </SoftButton>
                </SoftBox>
                </>
              )}
          </SoftBox>
        </SoftBox>
      </Card>
      <SoftSnackbar
        color="dark"
        icon="notifications"
        title={snackbar.title}
        content={snackbar.message}
        open={snackbar.show}
        close={hideSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      />
    </BasicLayout>
  );
}

export default Basic;
